import {
  type EnvironmentProviders,
  Injectable,
  inject,
  makeEnvironmentProviders,
} from '@angular/core';
import {
  BUS_ANALYTICS_SINK,
  type BusAnalyticsSink,
  type BusEvent,
  provideEventBusAnalytics,
} from '@tvd/cdk/events';
import { PostHogService } from './posthog.service';

/**
 * Concrete {@link BusAnalyticsSink} that forwards bus events to PostHog.
 *
 * Each dispatched event becomes a PostHog `capture` via
 * `PostHogService.track(event.type, payload)`, where the payload is the event's
 * own fields (everything except the `type` discriminant). `track` already
 * gates on analytics consent and swallows backend errors, so this sink is safe
 * to subscribe to the immortal root stream.
 *
 * Lives in the lib that owns the PostHog dependency: `@tvd/cdk/events` stays
 * decoupled from any concrete backend (data-access → cdk, never the reverse).
 */
@Injectable({ providedIn: 'root' })
export class PostHogBusSink implements BusAnalyticsSink {
  readonly #postHog = inject(PostHogService);

  forward(event: BusEvent): void {
    const { type, ...payload } = event;
    this.#postHog.track(type, payload);
  }
}

/**
 * Opt-in: wire the event bus to PostHog analytics.
 *
 * Binds {@link BUS_ANALYTICS_SINK} to {@link PostHogBusSink} and activates
 * forwarding via {@link provideEventBusAnalytics}. Off by default — only apps
 * that call this in their providers get bus events forwarded to PostHog.
 */
export function providePostHogBusAnalytics(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: BUS_ANALYTICS_SINK, useExisting: PostHogBusSink },
    provideEventBusAnalytics(),
  ]);
}
